import {h, Component} from 'preact'
import {HomepageElement} from '../homepage'
import TextButton from '../ui-components/TextButton'
import Icon, {IconType} from '../assets'
import { GenericHomepageElementTypes} from '../homepage/index'
import EmptyPlaceholder from './placeholder'
import WidgetConfigurator from './configurator'
import {ElementChange, DonePayload, MovePayload, VisiblePayload} from './toolbar'
import {isHtmlElement, bindPreactComponents} from '../../libs/preact-entry'
import Axios from 'axios'
import getWithInMemoryCache from '../../utils/getWithInMemoryCache'

interface Props {
  parentEl: HTMLElement,
  idx: number,
  edit: boolean,
  isMovable: boolean,
  homepageElement: HomepageElement,
  totalCount: number,
  onEdit: () => void,
  onClose: () => void,
  onMove: (m: MovePayload) => void,
  onRemove: () => void,
  onDone: (change: DonePayload) => void,
  onVisibleToggle: (v: VisiblePayload) => void
}

interface State {
  isConfigured: boolean,
}

export const typeMap = {
  'hero_banner': 'Hero Banner',
  'html_widget': 'HTML Widget',
  'quick_links': 'Quick Links',
  'knowledge_base': 'Knowledge Base',
  'recommendations': 'Recommendations',
  'featured_topics': 'Featured Topics',
  'category_and_topics_stream': 'Community content and sidebar',
  [GenericHomepageElementTypes.header]: 'Header',
  [GenericHomepageElementTypes.footer]: 'Footer',
  [GenericHomepageElementTypes.navigation_bar]: 'Navigation',
  [GenericHomepageElementTypes.statistics_bar]: 'Statistics',
  [GenericHomepageElementTypes.introduction_bar]: 'Introduction',
  [GenericHomepageElementTypes.feature_topics]: 'Featured Topics (moderator tags)',
}

interface TabButtonProps {
  onClick: () => void
  icon: IconType
}

const TabButton = ({onClick, icon}: TabButtonProps) => {
  return <div class={`homepage-widget-tab__button homepage-widget-tab__button--${icon}`}>
    <TextButton onClick={onClick}>
      <Icon size={20} icon={icon} color="currentColor"/>
    </TextButton>
  </div>
}

function isEditableWidget (element: HomepageElement): boolean {
  return [
    'knowledge_base',
    'html_widget',
    'recommendations',
    'hero_banner',
    'category_and_topics_stream',
    'featured_topics',
    'recommendations',
    'quick_links'
  ].indexOf(element.type) >= 0
}

function isRemovableWidget (element: HomepageElement) {
  return  ['html_widget'].indexOf(element.type) >= 0
}

export default class HomepageEditor extends Component<Props, State> {
  placeholder?: HTMLElement

  constructor (props: Props) {
    super(props)
    this.state = {
      isConfigured: true
    }
    this.onChange = this.onChange.bind(this)
  }

  componentDidMount () {
    const placeholder = this.props.parentEl.querySelector('.homepage-widget-placeholder')
    if (isHtmlElement(placeholder)) {
      this.placeholder = placeholder
    }
    this.setConfigured()
  }

  setConfigured (): void {
    if (!this.placeholder) return this.renderEmptyPlaceholder()
    const children = this.placeholder.children
    if (!children.length && !this.placeholder.textContent?.trim()) return this.renderEmptyPlaceholder()

    switch (this.props.homepageElement.type) {
      case 'introduction_bar':
        return this.isIntroductionConfigured(children)
      case 'html_widget':
        return this.isHTMLConfigured(children)
      case 'knowledge_base':
        this.isKnowledgeBaseConfigured(children)
        return
      case 'feature_topics':
        return this.isLegacyFeatureTopicsConfigured(children)
      case 'recommendations':
        return this.isRecommendationsConfigured(children)
      case 'quick_links':
        return this.isQuickLinksConfigured(children)
      case 'featured_topics':
        return this.isFeaturedTopicsConfigured(children)
    }
  }

  renderEmptyPlaceholder () {
    this.setState({isConfigured: false})
  }

  isIntroductionConfigured (children: HTMLCollection) {
    setTimeout(() => {
      if(children[0].classList.contains('is-transition--height-hidden')) {
        this.renderEmptyPlaceholder()
      }
    }, 0)
  }

  async isKnowledgeBaseConfigured (children: HTMLCollection) {
    const res = await getWithInMemoryCache('/site/knowledgeBase', {})
    if(!res.categoryGroups?.length) {
      this.renderEmptyPlaceholder()
    }
  }

  isLegacyFeatureTopicsConfigured (children: HTMLCollection) {
    if(!children[0].children.length) {
      this.renderEmptyPlaceholder()
    }
  }

  isHTMLConfigured (children: HTMLCollection) {
    if(!children[0].children.length && !children[0].textContent?.trim()) {
      this.renderEmptyPlaceholder()
    }
  }

  isQuickLinksConfigured (children: HTMLCollection) {
    const el = children[0]
    let data = []
    if (!isHtmlElement(el)) return
    try {
      const props = JSON.parse(el.dataset.props || '{}')
      data = props.data
    } catch (err) {}
    if (data.length) return
    this.renderEmptyPlaceholder()
  }

  isRecommendationsConfigured (children: HTMLCollection) {
    let data = []
    const el = children[0]
    if (!isHtmlElement(el)) return
    try {
      data = JSON.parse(el.dataset.props || '{}').data
    } catch (err) {}
    if (data.length) return
    this.renderEmptyPlaceholder()
  }

  isFeaturedTopicsConfigured (children: HTMLCollection) {
    let data = []
    const el = children[0]

    if (!isHtmlElement(el)) return
    try {
      data = JSON.parse(el.dataset.props || '{}').topicIds
    } catch (err) {}
    if (data?.length) return
    this.renderEmptyPlaceholder()
  }

  moveNode (up: boolean): void {
    this.props.onMove({direction: up ? 'up' : 'down'})
  }

  toggleVisible () {
    const active = !this.props.homepageElement.active
    if (active) {
      this.props.parentEl.classList.remove('homepage-widget-container--inactive')
    } else {
      this.props.parentEl.classList.add('homepage-widget-container--inactive')
    }
    this.props.onVisibleToggle(active)
  }

  toggleEdit () {
    this.props.onEdit()
  }

  remove () {
    this.props.onRemove()
  }

  async onChange (element: ElementChange) {
    const newElement = Object.assign({}, this.props.homepageElement, element)
    this.setState({
      isConfigured: true
    })
    const parent = this.props.parentEl
    this.props.onDone({element, idx: this.props.idx})

    const res = await Axios.get('/widget/render', {
      params: {
        type: newElement.type,
        title: element.title,
        config: JSON.stringify(element.config)
      }
    })
    const el = parent.querySelector('.homepage-widget-placeholder')
    if (!isHtmlElement(el)) return
    el.innerHTML = res.data
    bindPreactComponents(el)
    this.setConfigured()
  }

  render () {
    const canMoveUp = this.props.isMovable && this.props.idx > 2
    const canMoveDown = this.props.isMovable && this.props.idx < this.props.totalCount - 2

    return <div class={`homepage-widget-controls homepage-widget-controls--${this.props.homepageElement.type}`}>
      <div class="homepage-widget-tab">
        <span style={{marginRight: '10px'}}>{typeMap[this.props.homepageElement.type]}</span>

        <TabButton icon={this.props.homepageElement.active ? 'eye' : 'eye-cross'} onClick={() => this.toggleVisible()} />

        { isEditableWidget(this.props.homepageElement) && <TabButton icon="edit" onClick={() => this.toggleEdit()} /> }

        { isRemovableWidget(this.props.homepageElement) && <TabButton icon="trash" onClick={() => this.remove()} /> }

        { canMoveUp && <TabButton icon="arrow-up" onClick={() => this.moveNode(true)} /> }

        { canMoveDown && <TabButton icon="arrow-down" onClick={() => this.moveNode(false)} /> }

      </div>
      {!this.props.homepageElement.active ? <div class="homepage-widget-non-active-state">
        <Icon icon="eye-cross" color="currentColor" size={30}/>
      </div> : null}

      { this.state.isConfigured
      ? null
      : <EmptyPlaceholder type={this.props.homepageElement.type} placeHolder={this.placeholder}/> }

        { this.props.edit
          ? <WidgetConfigurator
              onChange={this.onChange}
              onClose={() => this.props.onClose()}
              homepageElement={this.props.homepageElement} />
          : null }
    </div>
  }
}

